/**
 * Maps Groups API DTOs to UI domain models and request payloads.
 */
import { GROUP_PRIVACY, GROUP_STATUS, POST_FEELINGS } from '../../constants';
import { getFullImageUrl } from '../../utils/imageUrl';
import { resolveLayoutForAlbum } from '../../utils/postImageLayout';
import { formatDate, formatPostCreatedAt, parseToDate } from '../../utils/dateHelpers';
import type {
  Group,
  GroupDetails,
  GroupFormData,
  GroupPost,
  GroupSortOption,
  GroupTypeFilter,
} from '../../types';
import type { PostImageLayout } from '../../types/post';
import type { GroupMember } from '../../types/groupDetails';
import { GROUP_ROLE, GROUP_ROLE_ID } from './groupsTypes';
import type {
  ApiFeedItem,
  ApiGroupDetailRecord,
  ApiGroupJoinRequestItem,
  ApiGroupListItem,
  ApiGroupMemberRecord,
  ApiGroupRecord,
  ApiMyGroupItem,
  ApiGroupSortBy,
  ApiGroupTypeFilter,
  CreateGroupRequest,
  GroupRoleId,
  GroupRoleType,
} from './groupsTypes';
import type { PendingApprovalRequest } from '../../types/groupDetails';

const parseFeelingFromTitle = (title?: string | null): string | null => {
  if (!title || typeof title !== 'string') return null;
  const trimmed = title.trim();
  if (!trimmed) return null;
  const lower = trimmed.toLowerCase();
  const byKey = POST_FEELINGS.find(
    f => lower === f.key || lower.endsWith(` ${f.key}`) || lower.includes(f.key),
  );
  return byKey ? byKey.key : trimmed;
};

export const parseNumericGroupId = (groupId?: string | number): number => {
  if (typeof groupId === 'number' && Number.isFinite(groupId)) {
    return groupId;
  }
  const parsed = Number.parseInt(String(groupId ?? ''), 10);
  if (!Number.isFinite(parsed)) {
    throw new Error('Invalid group id');
  }
  return parsed;
};

/** True when groupRole / role id indicates Owner, Admin, or Host. */
export const isAdminOrHostRole = (groupRole?: string | number | null): boolean => {
  if (groupRole == null || groupRole === '') return false;
  const raw = String(groupRole).trim().toLowerCase();
  if (!raw) return false;
  if (raw === '1' || raw === '2') return true; // Owner / Admin role ids
  return (
    raw.includes('owner') ||
    raw.includes('admin') ||
    raw.includes('host') ||
    raw.includes('creator')
  );
};

export const mapSortOptionToApi = (
  sort?: GroupSortOption | string,
): ApiGroupSortBy => {
  switch (sort) {
    case 'oldest':
      return 'Oldest';
    case 'members':
      return 'MostMembers';
    case 'alpha_asc':
      return 'AToZ';
    case 'alpha_desc':
      return 'ZToA';
    case 'newest':
    default:
      return 'Newest';
  }
};

export const mapGroupTypeToApi = (
  groupType?: GroupTypeFilter | string,
): ApiGroupTypeFilter => {
  switch (groupType) {
    case 'public':
      return 'Public';
    case 'private':
      return 'Private';
    case 'all':
    default:
      return 'All';
  }
};

/**
 * Role names are the wire format for AddMember / ChangeMemberRole
 * (`GroupRoleType`). Legacy numeric ids are accepted so a caller holding a
 * `GROUP_ROLE_ID` value keeps working.
 */
export const toGroupRoleType = (
  role: GroupRoleType | GroupRoleId | number | string,
): GroupRoleType => {
  const raw = String(role).trim().toLowerCase();
  if (raw === 'owner' || raw === String(GROUP_ROLE_ID.Owner)) {
    return GROUP_ROLE.Owner;
  }
  if (raw === 'admin' || raw === String(GROUP_ROLE_ID.Admin)) {
    return GROUP_ROLE.Admin;
  }
  return GROUP_ROLE.Member;
};

const mapMembershipStatus = (
  membershipStatus?: string,
  isJoined?: boolean,
): Group['status'] => {
  const normalized = (membershipStatus ?? '').toLowerCase().replace(/\s/g, '');
  if (
    normalized.includes('pending') ||
    normalized === 'requested' ||
    normalized === 'pendingapproval'
  ) {
    return GROUP_STATUS.PENDING;
  }
  if (
    normalized.includes('owner') ||
    normalized === 'groupowner' ||
    normalized === 'creator'
  ) {
    return GROUP_STATUS.OWNER;
  }
  if (
    normalized.includes('joined') ||
    normalized === 'member' ||
    normalized === 'active' ||
    isJoined === true
  ) {
    return GROUP_STATUS.JOINED;
  }
  if (normalized.includes('notmember') || normalized === 'none') {
    return GROUP_STATUS.JOIN_NOW;
  }
  return GROUP_STATUS.JOIN_NOW;
};

export interface ApiGroupMembershipFields {
  memberStatus?: string;
  membershipStatus?: string;
  isOwner?: boolean;
}

/** Maps backend memberStatus / isOwner to UI group status. */
export const mapGroupStatusFromApi = (
  item: ApiGroupMembershipFields,
): Group['status'] => {
  if (item.isOwner === true) {
    return GROUP_STATUS.OWNER;
  }
  const raw = item.memberStatus ?? item.membershipStatus ?? '';
  const normalized = raw.toLowerCase().replace(/\s/g, '');
  if (normalized === 'joined') {
    return GROUP_STATUS.JOINED;
  }
  if (normalized === 'pending') {
    return GROUP_STATUS.PENDING;
  }
  if (normalized === 'notmember') {
    return GROUP_STATUS.JOIN_NOW;
  }
  return mapMembershipStatus(raw);
};

const mapPrivacy = (isPrivate?: boolean): string =>
  isPrivate ? GROUP_PRIVACY.PRIVATE.toLowerCase() : GROUP_PRIVACY.PUBLIC.toLowerCase();

export const mapApiListItemToGroup = (item: ApiGroupListItem): Group => ({
  id: String(item.groupId),
  groupName: item.name,
  groupDescription: item.description ?? '',
  groupImage: item.iconUrl ? getFullImageUrl(item.iconUrl) : null,
  groupCoverImage: item.bannerUrl ? getFullImageUrl(item.bannerUrl) : null,
  privacyType: mapPrivacy(item.isPrivate),
  totalMembers: item.memberCount ?? 0,
  status: mapGroupStatusFromApi(item),
  isOwner: item.isOwner,
  isAdmin: item.isAdmin,
  requireJoinApproval: item.requireJoinApproval,
  requirePostApproval: item.requirePostApproval,
  ownerUserId: item.ownerUserId,
  createdAt: item.createdAt,
  firstPostDate: item.firstPostDate,
  lastPostDate: item.lastPostDate,
  // Absent flags mean "not reported", and a group the API returns without them
  // is a live group; defaulting isActive to false would grey out every row.
  isActive: item.isActive !== false,
  isDeleted: item.isDeleted === true,
});

export const mapApiMyGroupToGroup = (item: ApiMyGroupItem): Group => ({
  id: String(item.groupId),
  groupName: item.name,
  groupDescription: item.description ?? '',
  groupImage: item.iconUrl ? getFullImageUrl(item.iconUrl) : null,
  groupCoverImage: item.bannerUrl ? getFullImageUrl(item.bannerUrl) : null,
  privacyType: mapPrivacy(item.isPrivate),
  totalMembers: item.memberCount ?? 0,
  status: mapGroupStatusFromApi(item),
  isOwner: item.isOwner,
  isAdmin: item.isAdmin,
});

export const mapApiGroupRecordToGroup = (
  record: ApiGroupRecord,
  status: Group['status'] = GROUP_STATUS.JOINED,
): Group => ({
  id: String(record.groupId),
  groupName: record.name,
  groupDescription: record.description ?? '',
  groupImage: record.iconUrl ? getFullImageUrl(record.iconUrl) : null,
  groupCoverImage: record.bannerUrl ? getFullImageUrl(record.bannerUrl) : null,
  privacyType: mapPrivacy(record.isPrivate),
  totalMembers: 1,
  status,
  requireJoinApproval: record.requireJoinApproval,
});

const pickFirst = <T>(value: T | T[] | undefined): T | undefined => {
  if (value == null) return undefined;
  return Array.isArray(value) ? value[0] : value;
};

const coerceBoolean = (value: boolean | number | undefined): boolean => {
  if (typeof value === 'boolean') return value;
  // DetailResponse.isOwner is documented as int32 (0/1). Do not treat arbitrary
  // non-zero ints (e.g. owner user ids) as true — that falsely marks non-owners
  // as owners and leads to ReviewJoinRequest 403s.
  if (value === 1) return true;
  if (value === 0) return false;
  return false;
};

const resolveIsOwnerFromDetail = (
  detail?: ApiGroupDetailRecord,
): boolean => {
  if (coerceBoolean(detail?.isOwner)) return true;
  const status = String(
    detail?.currentUserMembershipStatus ??
      detail?.membershipStatus ??
      detail?.memberStatus ??
      '',
  )
    .toLowerCase()
    .replace(/\s/g, '');
  return (
    status.includes('owner') ||
    status === 'groupowner' ||
    status.includes('creator')
  );
};

const resolveJoinRequestId = (
  item: ApiGroupJoinRequestItem & Record<string, unknown>,
): number | null => {
  const candidates = [
    item.groupJoinRequestId,
    item.GroupJoinRequestId,
    item.joinRequestId,
    item.JoinRequestId,
    item.id,
    item.Id,
  ];
  for (const candidate of candidates) {
    if (candidate == null || candidate === '') continue;
    const parsed =
      typeof candidate === 'number'
        ? candidate
        : Number.parseInt(String(candidate), 10);
    if (Number.isFinite(parsed) && parsed > 0) {
      return parsed;
    }
  }
  return null;
};

export interface MappedGroupDetailBundle {
  details: GroupDetails;
  members: GroupMember[];
  posts: GroupPost[];
  isOwner: boolean;
  isAdmin: boolean;
  requireJoinApproval: boolean;
  requirePostApproval: boolean;
  onlyAdminCanPost: boolean;
}

export const mapGroupDetailResponse = (
  payload: {
    detail?: ApiGroupDetailRecord;
    members?: ApiGroupMemberRecord[];
    activity?: {
      membersCount?: number;
      newJoinedCount?: number;
      newJoinRequestCount?: number;
      newPostCount?: number;
      todayPostCount?: number;
      thisMonthPostCount?: number;
    };
    feeds?: ApiFeedItem[];
    membershipStatus?: string;
  },
): MappedGroupDetailBundle => {
  const detail = payload.detail;
  const groupId = detail?.groupId ?? 0;
  const groupName = detail?.groupName ?? detail?.name ?? '';
  const membersCount =
    payload.activity?.membersCount ?? detail?.memberCount ?? payload.members?.length ?? 0;

  const isOwnerFlag = resolveIsOwnerFromDetail(detail);
  const isAdminFlag = detail?.isAdmin === true || isOwnerFlag;

  const status = mapGroupStatusFromApi({
    memberStatus: detail?.memberStatus,
    membershipStatus:
      payload.membershipStatus ??
      detail?.currentUserMembershipStatus ??
      detail?.membershipStatus,
    isOwner: isOwnerFlag,
  });

  const createdAt = detail?.createdAt;
  // GetGroupDetail does not send timezoneOffset; treat naive strings as UTC.
  const createdInstant = createdAt ? parseToDate(createdAt, 'utc') : null;
  const createdDate = createdInstant
    ? formatDate(createdInstant, 'MMM DD, YYYY')
    : '';

  const activityMembersLabel =
    membersCount === 1 ? '1 member' : `${membersCount} members`;

  const newJoined = payload.activity?.newJoinedCount ?? 0;
  // Use todaypostcount if available; fallback to activity.newPostCount
  const newPosts = payload.activity?.todayPostCount ?? detail?.todayPostCount ?? payload.activity?.newPostCount ?? 0;
  const monthPosts = payload.activity?.thisMonthPostCount ?? detail?.thisMonthPostCount ?? 0;

  const ownerId = detail?.ownerId ?? detail?.ownerUserId;

  const members: GroupMember[] = (payload.members ?? []).map((m, index) => {
    const memberId = m.memberId ?? m.userId ?? index;
    const joinedInstant = m.createdAt ? parseToDate(m.createdAt, 'utc') : null;
    const joined = joinedInstant
      ? formatDate(joinedInstant, 'MMM DD, YYYY')
      : '';
    const rawGuid =
      m.userGuid ??
      (m as Record<string, unknown>).UserGuid ??
      (m as Record<string, unknown>).profileUserGuid;
    const profileUserGuid =
      typeof rawGuid === 'string' && rawGuid.trim() ? rawGuid.trim() : undefined;
    const userId = m.userId ?? m.memberId ?? null;
    const matchesOwnerId =
      ownerId != null && userId != null && Number(userId) === Number(ownerId);
    const roleLower = String(m.groupRole ?? '').toLowerCase();
    const isOwnerMember = matchesOwnerId || roleLower.includes('owner');
    const isAdminMember =
      isOwnerMember || m.isGroupAdmin === true || isAdminOrHostRole(m.groupRole);

    return {
      id: String(memberId),
      userId,
      name: m.userName ?? m.firstName ?? '',
      username: m.userName ?? m.firstName ?? '',
      profileImage: (m.userProfile || m.profileImage
        ? getFullImageUrl(m.userProfile || m.profileImage)
        : getFullImageUrl(null)) || '',
      joinedDate: joined,
      profileUserGuid,
      groupRole: m.groupRole,
      isOwner: isOwnerMember,
      isAdmin: isAdminMember,
      approvalStatus: m.approveStatus ?? undefined,
      approvedBy: m.approveName ?? undefined,
    };
  });

  const ownerMember =
    ownerId != null
      ? members.find(
          m =>
            (m.userId != null && Number(m.userId) === Number(ownerId)) ||
            Number(m.id) === Number(ownerId),
        )
      : members.find(m =>
          String(m.username || m.name).toLowerCase() ===
          String(detail?.ownerUserName || '').toLowerCase(),
        );

  // Ensure owner is flagged even if groupRole was missing from the payload
  if (ownerMember) {
    ownerMember.isOwner = true;
    ownerMember.isAdmin = true;
  }

  const details: GroupDetails = {
    id: String(groupId),
    groupName,
    groupDescription: detail?.description ?? '',
    groupCoverImage: detail?.bannerUrl
      ? getFullImageUrl(detail.bannerUrl)
      : null,
    groupProfileImage: detail?.iconUrl
      ? getFullImageUrl(detail.iconUrl)
      : null,
    createdDate,
    privacyType: mapPrivacy(detail?.isPrivate),
    totalMembers: membersCount,
    status,
    isOwner: isOwnerFlag,
    isAdmin: isAdminFlag,
    requireJoinApproval: detail?.requireJoinApproval ?? false,
    requirePostApproval: detail?.requirePostApproval ?? false,
    onlyAdminCanPost: detail?.onlyAdminCanPost ?? false,
    admin: {
      name: detail?.ownerUserName ?? ownerMember?.name ?? '',
      role: 'Group Creator',
      avatar: detail?.ownerPhoto
        ? getFullImageUrl(detail.ownerPhoto)
        : ownerMember?.profileImage ?? null,
      profileUserGuid: ownerMember?.profileUserGuid ?? null,
      userId: ownerId ?? ownerMember?.userId ?? null,
    },
    activity: {
      postsToday: newPosts > 0 ? `${newPosts} new posts` : 'No new posts',
      postsThisMonth: monthPosts > 0 ? `${monthPosts} posts` : 'No posts',
      totalMembers: activityMembersLabel,
      newMembersThisWeek: newJoined > 0 ? `+${newJoined}` : '+0',
      postCount: newPosts,
      memberCount: membersCount,
      newMemberCount: newJoined,
      monthPostCount: monthPosts,
      joinRequestCount: payload.activity?.newJoinRequestCount ?? 0,
    },
  };

  const posts = (payload.feeds ?? []).map(feed => {
    const mapped = mapFeedItemToGroupPost(feed);
    const groupIcon = detail?.iconUrl ? getFullImageUrl(detail.iconUrl) : null;
    return groupIcon ? { ...mapped, groupImage: groupIcon } : mapped;
  });

  return {
    details,
    members,
    posts,
    isOwner: isOwnerFlag,
    isAdmin: isAdminFlag,
    requireJoinApproval: detail?.requireJoinApproval ?? false,
    requirePostApproval: detail?.requirePostApproval ?? false,
    onlyAdminCanPost: detail?.onlyAdminCanPost ?? false,
  };
};

/** Group icon on feed rows when the API sends it; never the author's userPhoto. */
const resolveFeedGroupIcon = (feed: ApiFeedItem): string | null => {
  const raw = feed as ApiFeedItem & {
    iconUrl?: string;
    groupPhoto?: string;
    groupIcon?: string;
    groupImage?: string;
  };
  const iconPath =
    raw.iconUrl ?? raw.groupPhoto ?? raw.groupIcon ?? raw.groupImage;
  if (typeof iconPath !== 'string' || !iconPath.trim()) return null;
  return getFullImageUrl(iconPath.trim()) || iconPath.trim();
};

export const mapFeedItemToGroupPost = (feed: ApiFeedItem): GroupPost => {
  const postDate = feed.postDate ?? new Date().toISOString();
  const contentType = (feed.contentTypeName || '').toLowerCase().trim();
  const isVideo = contentType === 'video';
  const postId = feed.postId;
  const albumUris = !isVideo && Array.isArray(feed.photoAlbum)
    ? feed.photoAlbum
        .map(item => {
          const raw = typeof item === 'string' ? item.trim() : '';
          if (!raw) return null;
          return getFullImageUrl(raw) || raw;
        })
        .filter((uri): uri is string => !!uri)
    : [];
  const mediaUri = albumUris[0]
    ?? (feed.url ? (getFullImageUrl(feed.url) || feed.url) : undefined)
    ?? null;
  const rawGuid =
    feed.profileUserGuid ??
    (feed as unknown as Record<string, unknown>).ProfileUserGuid ??
    (feed as unknown as Record<string, unknown>).userGuid;
  const profileUserGuid =
    typeof rawGuid === 'string' && rawGuid.trim() ? rawGuid.trim() : null;

  // The group feed answers with the album but no layout, so the count decides
  // it exactly as the composer did on the way up.
  const imageLayout: PostImageLayout | null = isVideo
    ? null
    : resolveLayoutForAlbum(albumUris.length || (mediaUri ? 1 : 0), null);

  const resolvedPostId =
    postId != null && /^\d+$/.test(String(postId).trim())
      ? String(postId).trim()
      : null;

  return {
    id: resolvedPostId ?? `feed-${feed.userId ?? 0}-${postDate}`,
    groupId: feed.groupId != null ? String(feed.groupId) : '',
    groupName: feed.groupName ?? '',
    groupImage: resolveFeedGroupIcon(feed),
    userId: feed.userId != null ? String(feed.userId) : '',
    userName: feed.userName ?? '',
    firstName: feed.firstName ?? null,
    currentWeight: feed.currentWeight != null ? `${feed.currentWeight} ${feed.scaleUnitWeightName ?? ''}`.trim() : undefined,
    userProfileImage: feed.userPhoto ? getFullImageUrl(feed.userPhoto) : null,
    postDescription: feed.bodyText ?? '',
    feeling: parseFeelingFromTitle(feed.title),
    postImage: !isVideo && mediaUri ? mediaUri : null,
    images:
      !isVideo && albumUris.length > 1
        ? albumUris.map(uri => ({ uri }))
        : undefined,
    imageLayout,
    postVideo: isVideo && mediaUri ? mediaUri : null,
    thumbnail: isVideo && feed.thumbnailUrl ? (getFullImageUrl(feed.thumbnailUrl) || feed.thumbnailUrl) : null,
    // Group feed reads send `timezoneOffset`; the API answers in device time.
    createdAt: formatPostCreatedAt(postDate, 'local'),
    postDate,
    likeCount: feed.reactedCount ?? 0,
    commentCount: feed.commentCount ?? 0,
    shareCount: feed.totalShares ?? 0,
    liked: typeof feed.isReacted === 'boolean' ? feed.isReacted : Boolean(feed.isReacted),
    saved: false,
    profileUserGuid,
    allowShare: feed.allowShare !== false,
  };
};

/**
 * Narrows a loaded group detail back to the list-shaped `Group`, so screens
 * that only ever fetched the detail (deep links, group page) can still drive
 * the shared join/leave flows.
 */
export const mapGroupDetailsToGroup = (details: GroupDetails): Group => ({
  id: details.id,
  groupName: details.groupName,
  groupImage: details.groupProfileImage,
  groupCoverImage: details.groupCoverImage,
  groupDescription: details.groupDescription,
  privacyType: details.privacyType,
  totalMembers: details.totalMembers,
  status: details.status,
  isOwner: details.isOwner,
  isAdmin: details.isAdmin,
  requireJoinApproval: details.requireJoinApproval,
  requirePostApproval: details.requirePostApproval,
  onlyAdminCanPost: details.onlyAdminCanPost,
});

export const mapJoinRequestToPendingApproval = (
  item: ApiGroupJoinRequestItem & Record<string, unknown>,
): PendingApprovalRequest | null => {
  const groupJoinRequestId = resolveJoinRequestId(item);
  if (groupJoinRequestId == null) {
    return null;
  }

  const firstName =
    (item.firstName as string | undefined) ??
    (item.FirstName as string | undefined);
  const userName =
    (item.userName as string | undefined) ??
    (item.UserName as string | undefined);
  const profileImage =
    (item.profileImage as string | undefined) ??
    (item.ProfileImage as string | undefined);

  return {
    id: String(groupJoinRequestId),
    groupJoinRequestId,
    profileUserGuid:
      typeof item.userGuid === 'string' && item.userGuid.trim()
        ? item.userGuid.trim()
        : typeof (item as Record<string, unknown>).UserGuid === 'string'
          ? String((item as Record<string, unknown>).UserGuid).trim()
          : null,
    name: firstName || userName || '',
    profileImage: (profileImage
      ? getFullImageUrl(profileImage)
      : getFullImageUrl(null)) || '',
    mutualMembers: 0,
    invitedBy: null,
    isOnline: false,
  };
};

export const normalizeGroupDetailPayload = (
  raw: {
    groupDetail?: ApiGroupDetailRecord | ApiGroupDetailRecord[];
    groupMembers?: ApiGroupMemberRecord[];
    groupActivity?:
      | { membersCount?: number; newJoinedCount?: number; newPostCount?: number; todayPostCount?: number; thisMonthPostCount?: number }
      | Array<{
          membersCount?: number;
          newJoinedCount?: number;
          newPostCount?: number;
          todayPostCount?: number;
          thisMonthPostCount?: number;
        }>;
    feeds?: ApiFeedItem[];
    membershipStatus?: string;
  },
  requestedGroupId?: number,
): MappedGroupDetailBundle => {
  const pickDetail = (): ApiGroupDetailRecord | undefined => {
    const value = raw.groupDetail;
    if (value == null) return undefined;
    const list = Array.isArray(value) ? value : [value];
    if (requestedGroupId != null) {
      const match = list.find(
        item => item?.groupId === requestedGroupId,
      );
      if (match) return match;
    }
    return list.find(item => item?.groupId != null) ?? list[0];
  };

  const detail = pickDetail();
  const activity = pickFirst(raw.groupActivity);
  if (!detail?.groupId) {
    throw new Error('Group detail not found');
  }
  return mapGroupDetailResponse({
    detail,
    members: raw.groupMembers,
    activity,
    feeds: raw.feeds,
    membershipStatus:
      raw.membershipStatus ??
      detail?.currentUserMembershipStatus ??
      detail?.membershipStatus,
  });
};

const resolveImageFieldForApi = (
  value: unknown,
): string | undefined => {
  if (value == null || value === '') return undefined;
  if (typeof value === 'number') return undefined;
  if (typeof value === 'string') {
    if (value.startsWith('file:') || value.startsWith('content:')) {
      return undefined;
    }
    if (value.startsWith('http://') || value.startsWith('https://')) {
      return value;
    }
    return value;
  }
  return undefined;
};

export const mapFormToCreateRequest = (
  form: GroupFormData,
): CreateGroupRequest => {
  const name =
    (form.groupName as string | undefined) ??
    (form.name as string | undefined) ??
    '';
  const description =
    (form.groupDescription as string | undefined) ??
    (form.description as string | undefined) ??
    '';

  const privacyRaw =
    (form.privacyType as string | undefined) ??
    (form.privacy as string | undefined) ??
    GROUP_PRIVACY.PUBLIC;
  const isPrivate =
    String(privacyRaw).toLowerCase() === 'private' ||
    String(privacyRaw) === GROUP_PRIVACY.PRIVATE;

  const requireJoinApproval =
    typeof form.requireApproval === 'boolean'
      ? form.requireApproval
      : true;

  const iconUrl =
    resolveImageFieldForApi(form.groupImage) ??
    resolveImageFieldForApi(form.groupProfileImage) ??
    resolveImageFieldForApi(form.avatarImage);

  const bannerUrl =
    resolveImageFieldForApi(form.groupCoverImage) ??
    resolveImageFieldForApi(form.coverImage);

  const request: CreateGroupRequest = {
    name: name.trim(),
    description: description.trim(),
    isPrivate,
    requireJoinApproval,
    requirePostApproval: form.requirePostApproval === true,
    onlyAdminCanPost: form.onlyAdminCanPost === true,
  };

  if (iconUrl) request.iconUrl = iconUrl;
  if (bannerUrl) request.bannerUrl = bannerUrl;

  return request;
};

const createBinaryFileObj = (src: any, defaultName: string) => {
  if (!src) return undefined;
  // Web pickers hand back a File/Blob, which FormData can take as-is. Native
  // pickers only give a uri, so those are wrapped below instead.
  if (typeof Blob !== 'undefined' && src instanceof Blob) {
    return src;
  }
  if (typeof src === 'object' && 'file' in src && src.file) {
    return src.file;
  }
  if (typeof src === 'object' && 'uri' in src && src.uri) {
    let name = src.fileName || src.name || defaultName;
    if (!name.includes('.')) {
      name += '.jpg';
    }
    return {
      uri: src.uri,
      type: src.type || src.mediaType || 'image/jpeg',
      name: name,
    };
  }
  if (typeof src === 'string' && (src.startsWith('file:') || src.startsWith('content:') || src.startsWith('ph:'))) {
    let filename = src.split('/').pop() || defaultName;
    if (!filename.includes('.')) {
      filename += '.jpg';
    }
    const ext = filename.split('.').pop()?.toLowerCase();
    const type = ext === 'png' ? 'image/png' : 'image/jpeg';
    return {
      uri: src,
      type,
      name: filename,
    };
  }
  return undefined;
};

export const mapFormToCreateFormData = (form: GroupFormData): FormData => {
  const req = mapFormToCreateRequest(form);
  const formData = new FormData();

  formData.append('Name', req.name);
  formData.append('Description', req.description);
  formData.append('IsPrivate', String(req.isPrivate));
  formData.append('RequireJoinApproval', String(req.requireJoinApproval));
  formData.append('RequirePostApproval', String(req.requirePostApproval ?? false));
  formData.append('OnlyAdminCanPost', String(req.onlyAdminCanPost ?? false));

  const avatarSource = form.groupImage || form.groupProfileImage || form.avatarImage;
  const avatarFile = createBinaryFileObj(avatarSource, 'avatar.jpg');
  if (avatarFile) {
    formData.append('IconPicture', avatarFile as any);
  }

  const coverSource = form.groupCoverImage || form.coverImage;
  const coverFile = createBinaryFileObj(coverSource, 'cover.jpg');
  if (coverFile) {
    formData.append('BannerPicture', coverFile as any);
  }

  return formData;
};

export const mapFormToUpdateFormData = (
  groupId: string,
  form: GroupFormData,
): FormData => {
  const req = mapFormToCreateRequest(form);
  const formData = new FormData();

  formData.append('GroupId', String(parseNumericGroupId(groupId)));
  formData.append('Name', req.name);
  formData.append('Description', req.description);
  formData.append('IsPrivate', String(req.isPrivate));
  formData.append('RequireJoinApproval', String(req.requireJoinApproval));
  formData.append('RequirePostApproval', String(req.requirePostApproval ?? false));
  formData.append('OnlyAdminCanPost', String(req.onlyAdminCanPost ?? false));

  const avatarSource = form.groupImage || form.groupProfileImage || form.avatarImage;
  const avatarFile = createBinaryFileObj(avatarSource, 'avatar.jpg');
  if (avatarFile) {
    formData.append('IconPicture', avatarFile as any);
  }

  const coverSource = form.groupCoverImage || form.coverImage;
  const coverFile = createBinaryFileObj(coverSource, 'cover.jpg');
  if (coverFile) {
    formData.append('BannerPicture', coverFile as any);
  }

  return formData;
};

export const hasLocalImagePendingUpload = (form: GroupFormData): boolean => {
  const candidates = [
    form.groupImage,
    form.groupProfileImage,
    form.avatarImage,
    form.groupCoverImage,
    form.coverImage,
  ];
  return candidates.some(
    v =>
      typeof v === 'string' &&
      (v.startsWith('file:') || v.startsWith('content:')),
  );
};
